import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import type { CrmObject, CrmObjectsListResponse } from '@/types/crm';

export interface UseDismissReminderResult {
  /** Hides a reminder from the current user's dashboard via the self-only
   * quickfetch dismiss endpoint. A call for an entity that already has a
   * dismiss in flight is a no-op. */
  dismissReminder: (reminder: CrmObject) => Promise<void>;
  /** Whether a dismiss request for this specific entity is currently in flight. */
  isDismissing: (reminderEntityId: string) => boolean;
}

/**
 * Returns `{ dismissReminder, isDismissing }` for hiding a reminder from the
 * current user's dashboard. `dismissReminder` calls the self-only dismiss
 * endpoint (`POST /api/orgs/<orgId>/quickfetch/dismiss/`), which appends the
 * caller's user entity id to the per-user dismissed set idempotently with
 * server-side conflict retry. Optimistically removes the reminder from the
 * dashboard list cache; on failure it restores only that specific entity into
 * the *current* cache (not a stale pre-call snapshot), so a concurrent
 * dismiss of a different reminder that already succeeded isn't resurrected.
 */
export function useDismissReminder(orgId: number): UseDismissReminderResult {
  const { userId } = useCurrentUserEntity();
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Set<string>>(new Set());
  // Ref mutations alone don't trigger a re-render, but callers (e.g. the
  // Dismiss button's `disabled` prop) need isDismissing() to reflect the
  // in-flight state immediately — this state bump forces that re-render.
  const [, forceRender] = useState(0);

  const isDismissing = useCallback(
    (reminderEntityId: string) => inFlightRef.current.has(reminderEntityId),
    []
  );

  const dismissReminder = useCallback(
    async (reminder: CrmObject) => {
      if (!orgId || !userId) {
        return;
      }

      if (inFlightRef.current.has(reminder.entity_id)) {
        logger.debug('Dismiss reminder: suppressed duplicate in-flight call', {
          reminderEntityId: reminder.entity_id,
          orgId,
        });
        return;
      }
      inFlightRef.current.add(reminder.entity_id);
      forceRender(n => n + 1);

      const key = crmQueryKeys.objects(orgId, 'ahoy_reminder');
      await queryClient.cancelQueries({ queryKey: key });

      // Capture only the entity being removed, per matching query — not the
      // whole list. A full-list snapshot would resurrect other reminders
      // that were legitimately dismissed by a different in-flight call once
      // this call's rollback restores it.
      const removedEntities = new Map<string, CrmObject>();
      queryClient
        .getQueriesData<CrmObjectsListResponse>({ queryKey: key })
        .forEach(([queryKey, data]) => {
          const removed = data?.results?.find(
            r => r.entity_id === reminder.entity_id
          );
          if (removed) {
            removedEntities.set(JSON.stringify(queryKey), removed);
          }
        });

      queryClient.setQueriesData<CrmObjectsListResponse>(
        { queryKey: key },
        old =>
          old && Array.isArray(old.results)
            ? {
                ...old,
                results: old.results.filter(
                  r => r.entity_id !== reminder.entity_id
                ),
              }
            : old
      );

      try {
        await apiPost(`/api/orgs/${orgId}/quickfetch/dismiss/`, {
          entity_id: reminder.entity_id,
        });
      } catch (error) {
        // Re-insert this entity into the cache as it stands right now, not
        // the stale pre-call snapshot — other dismiss calls may have
        // completed in the interim and their removals must survive.
        queryClient
          .getQueriesData<CrmObjectsListResponse>({ queryKey: key })
          .forEach(([queryKey, data]) => {
            const removed = removedEntities.get(JSON.stringify(queryKey));
            if (
              !removed ||
              !data ||
              !Array.isArray(data.results) ||
              data.results.some(r => r.entity_id === reminder.entity_id)
            ) {
              return;
            }
            queryClient.setQueryData<CrmObjectsListResponse>(queryKey, {
              ...data,
              results: [...data.results, removed],
            });
          });
        throw error;
      } finally {
        inFlightRef.current.delete(reminder.entity_id);
        forceRender(n => n + 1);
        // Prefix invalidation (no `exact: true`) is safe here only because reminders
        // still filter on the stateless `ahoy_dismissed_by_users` CRM property, so this
        // query key never changes across a dismiss/undismiss. Meetings moved to per-user
        // state in apps/meetings and therefore need `exact: true` scoped to old keys (see
        // useDismissMeeting.ts) to avoid clobbering the newly-seeded hidden-set key. If
        // reminders ever move to per-user state the same way, switch to that pattern too.
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    [orgId, userId, queryClient]
  );

  return { dismissReminder, isDismissing };
}
